import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * `data-screen-label="New campaign"` — the questions a campaign asks, written in place.
 *
 * The design reads a question set out of an uploaded CSV, DOCX or PDF and previews it as
 * numbered rows with a kind tag. Reading a document is plan-003's document-upload path, so
 * here the same rows are WRITTEN: one text and one kind per question, in order. What the API
 * receives is identical either way — `QuestionDraft[]` — so nothing about the campaign
 * changes when upload arrives; only how the rows are filled.
 */
import { Button, Input, Select } from "@wetalk/ui";
import type { ReactNode } from "react";

import { QUESTION_KIND_OPTION, type QuestionEntry } from "./campaign-copy.js";
import type { CampaignQuestionKindView } from "../../api/gateway.js";

/** The API's ceiling on one set (`QUESTION_MAX` in the campaign area). */
export const QUESTION_ENTRY_MAX = 50;

export interface CampaignQuestionEditorProps {
  /** What the rows are called in accessible names: "Question" or "Returning question". */
  readonly noun: string;
  readonly entry: readonly QuestionEntry[];
  readonly onChange: (next: readonly QuestionEntry[]) => void;
  /** Mints a React key for a new row; the screen owns the counter. */
  readonly nextKey: () => string;
}

function kindOf(raw: string): CampaignQuestionKindView {
  const found = QUESTION_KIND_OPTION.find((one) => one.value === raw);
  if (found === undefined) throw new Error(portalText("operations", "value_is_not_a_question_kind_008fbf2b", { v0: String(raw) }));
  return found.value;
}

export function CampaignQuestionEditor({ noun, entry, onChange, nextKey }: CampaignQuestionEditorProps): ReactNode {
  usePortalTranslation("operations");
  const replace = (index: number, next: QuestionEntry): void => {
    onChange(entry.map((one, at) => (at === index ? next : one)));
  };

  return (
    <div className="c-camp-editor">
      <ol className="c-camp-editor-rows">
        {entry.map((one, index) => (
          <li className="c-camp-editor-row" key={one.key}>
            <span className="c-camp-question-n">{index + 1}</span>
            <Input
              aria-label={`${noun} ${String(index + 1)}`}
              placeholder={portalText("operations", "on_a_scale_of_one_to_five_how_was_your_last_order_b449860f")}
              maxLength={500}
              value={one.text}
              onChange={(event) => {
                replace(index, { ...one, text: event.target.value });
              }}
            />
            <Select
              aria-label={portalText("operations", "kind_of_value_value_1618f7e1", { v0: String(noun.toLowerCase()), v1: String(String(index + 1)) })}
              value={one.kind}
              onChange={(event) => {
                replace(index, { ...one, kind: kindOf(event.target.value) });
              }}
            >
              {QUESTION_KIND_OPTION.map((kind) => (
                <option key={kind.value} value={kind.value}>
                  {kind.label}
                </option>
              ))}
            </Select>
            <Button
              variant="ghost"
              aria-label={portalText("operations", "remove_value_value_057df0ff", { v0: String(noun.toLowerCase()), v1: String(String(index + 1)) })}
              onClick={() => {
                onChange(entry.filter((_, at) => at !== index));
              }}
            >
              {portalText("operations", "remove_e963907d")}</Button>
          </li>
        ))}
      </ol>
      <Button
        variant="secondary"
        disabled={entry.length >= QUESTION_ENTRY_MAX}
        onClick={() => {
          onChange([...entry, { key: nextKey(), text: "", kind: "open" }]);
        }}
      >
        {portalText("operations", "add_a_question_076e5e91")}</Button>
    </div>
  );
}
